<template>
    <div class="content">
        <div class="container-fluid">
            <router-link class="font-italic pb-4" to="/billing/carriers">
                <i class="fa fa-chevron-circle-left mr-2"/>
                <span v-html="$t('Carrier.backToCarriers')"/>
            </router-link>
            <transition name="fade" mode="out-in">
                <APILoading v-if="carrier === undefined" :text="$t('Carrier.loadingCarrier')"
                            :color="$data.$colors.billing" key="loading"/>
                <ResourceNotFound v-else-if="carrier === null" key="not-found" category="billing"/>
                <div class="mt-2" v-else key="carrier" id="carrier">
                    <PageTitle :title="`${$t('Carrier.carrierCard')}<span class='pull-right'>${carrier.name}</span>`">
                        <template v-slot:left>
                            <i class="fa fa-truck fa-3x text-billing"/>
                        </template>
                    </PageTitle>
                    <div class="row">
                        <CarrierInfoCard @editCarrier="editCarrier" :carrier="carrier"/>
                    </div>
                    <div class="row">
                        <div class="col-lg-12">
                            <div class="card card-tabs">
                                <div class="card-body">
                                    <ul class="nav nav-pills float-right" role="tablist">
                                        <li class="nav-item">
                                            <a class="nav-link show active" id="destinations-tab" data-toggle="pill"
                                               href="#destinations" role="tab" aria-controls="destinations"
                                               aria-expanded="true" aria-selected="true">
                                                <i class="fa fa-flag mr-1"/>
                                                <span class="mr-1" v-html="$t('Carrier.destinations')"/>
                                                <span class="badge badge-light" v-html="carrier.destinations.length"/>
                                            </a>
                                        </li>
                                        <li class="nav-item">
                                            <a class="nav-link show" id="areas-tab" data-toggle="pill"
                                               href="#areas" role="tab" aria-controls="areas"
                                               aria-expanded="false" aria-selected="false">
                                                <i class="fas fa-globe mr-1"/>
                                                <span class="mr-1" v-html="$t('Carrier.areas')"/>
                                                <span class="badge badge-light" v-html="carrier.areas.length"/>
                                            </a>
                                        </li>
                                        <li class="nav-item">
                                            <a class="nav-link show" id="taxes-tab" data-toggle="pill"
                                               href="#taxes" role="tab" aria-controls="taxes"
                                               aria-expanded="false" aria-selected="false">
                                                <i class="fas fa-hand-holding-usd mr-1"/>
                                                <span class="mr-1" v-html="$t('Carrier.taxes')"/>
                                                <span class="badge badge-light" v-html="carrier.taxes.length"/>
                                            </a>
                                        </li>
                                    </ul>
                                    <h4 class="header-title m-b-30" v-html="$t('Carrier.manage')"/>
                                    <div class="tab-content">
                                        <div class="tab-pane fade show active" id="destinations" role="tabpanel" aria-labelledby="destinations-tab">
                                            <div class="row">
                                                <CarrierDestinationList @addCarrierDestination="addCarrierDestination" @editCarrierDestination="editCarrierDestination"
                                                                        @deleteCarrierDestination="deleteCarrierDestination" @editCarrierArea="editCarrierArea"
                                                                        :carrier="carrier"/>
                                            </div>
                                        </div>
                                        <div class="tab-pane fade" id="areas" role="tabpanel" aria-labelledby="areas-tab">
                                            <div class="row">
                                                <CarrierAreaList @addCarrierArea="addCarrierArea" @editCarrierArea="editCarrierArea"
                                                                 @deleteCarrierArea="deleteCarrierArea" @editCarrierDestination="editCarrierDestination"
                                                                 :carrier="carrier"/>
                                            </div>
                                        </div>
                                        <div class="tab-pane fade" id="taxes" role="tabpanel" aria-labelledby="taxes-tab">
                                            <div class="row">
                                                <CarrierTaxList @addCarrierTax="addCarrierTax" @editCarrierTax="editCarrierTax"
                                                              @deleteCarrierTax="deleteCarrierTax" :carrier="carrier"/>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </transition>
        </div>
    </div>
</template>

<script>
import { mapActions } from "vuex";
import PageTitle from "../../../shared/misc/PageTitle";
import APILoading from "../../../shared/API/APILoading";
import ResourceNotFound from "../../../shared/misc/ResourceNotFound";
import Carrier from "../../../../classes/Billing/Carrier/Carrier";
import CarrierInfoCard from "../../../shared/Billing/Carrier/CarrierInfoCard";
import CarrierTaxList from "./CarrierTaxList/CarrierTaxList";
import CarrierTax from "../../../../classes/Billing/Carrier/CarrierTax";
import CarrierDestinationList from "./CarrierDestinationList/CarrierDestinationList";
import CarrierDestination from "../../../../classes/Billing/Carrier/CarrierDestination";
import CarrierAreaList from "./CarrierAreaList/CarrierAreaList";
import CarrierArea from "../../../../classes/Billing/Carrier/CarrierArea";

export default {
    name: "Carrier",
    components: { CarrierAreaList, CarrierDestinationList, CarrierTaxList, CarrierInfoCard, ResourceNotFound, APILoading, PageTitle },
    data() {
        return {
            carrier: undefined,
        };
    },
    async created() {
        await this.checkUserAuthentication();
        if (!isNaN(this.carrierId) && this.carrierId > 0) {
            await this.getCarrier();
        } else {
            this.carrier = null;
        }
    },
    methods: {
        ...mapActions("user", {
            checkUserAuthentication: "checkUserAuthentication",
        }),
        async getCarrier() {
            try {
                const response = await this.$diceAPI.getCarrier(this.carrierId);
                this.carrier = new Carrier(response.data);
            } catch (e) {
                if (e.response.status === 404) {
                    this.carrier = null;
                } else {
                    this.$displayError(e);
                }
            }
        },
        editCarrier(carrier) {
            this.carrier = new Carrier(carrier);
        },
        addCarrierDestination(carrierDestination) {
            this.carrier.destinations.push(new CarrierDestination(carrierDestination));
        },
        editCarrierDestination(newCarrierDestination) {
            const index = this.carrier.destinations.findIndex(destination => destination.id === newCarrierDestination.id);
            this.carrier.destinations.splice(index, 1, new CarrierDestination(newCarrierDestination));
        },
        deleteCarrierDestination(carrierDestination) {
            const index = this.carrier.destinations.findIndex(destination => destination.id === carrierDestination.id);
            this.carrier.destinations.splice(index, 1);
        },
        addCarrierTax(carrierTax) {
            this.carrier.taxes.push(new CarrierTax(carrierTax));
        },
        editCarrierTax(newCarrierTax) {
            const index = this.carrier.taxes.findIndex(tax => tax.id === newCarrierTax.id);
            this.carrier.taxes.splice(index, 1, new CarrierTax(newCarrierTax));
        },
        deleteCarrierTax(carrierTax) {
            const index = this.carrier.taxes.findIndex(tax => tax.id === carrierTax.id);
            this.carrier.taxes.splice(index, 1);
        },
        addCarrierArea(carrierArea) {
            this.carrier.areas.push(carrierArea);
            for (const destination of this.carrier.destinations) {
                if (carrierArea.destinations.find(d => d.id === destination.id)) {
                    this.deleteCarrierAreaDestination(destination.carrierAreaId, destination.id);
                    destination.carrierAreaId = carrierArea.id;
                    destination.area = new CarrierArea(carrierArea);
                }
            }
        },
        editCarrierArea(newCarrierArea) {
            const index = this.carrier.areas.findIndex(area => area.id === newCarrierArea.id);
            this.carrier.areas.splice(index, 1, newCarrierArea);
            for (const destination of this.carrier.destinations) {
                if (destination.carrierAreaId === newCarrierArea.id && newCarrierArea.destinations.find(d => d.id === destination.id) === undefined) {
                    destination.carrierAreaId = null;
                    destination.area = new CarrierArea();
                } else if (newCarrierArea.destinations.find(d => d.id === destination.id)) {
                    if (destination.carrierAreaId && destination.carrierAreaId !== newCarrierArea.id) {
                        this.deleteCarrierAreaDestination(destination.carrierAreaId, destination.id);
                    }
                    destination.carrierAreaId = newCarrierArea.id;
                    destination.area = new CarrierArea(newCarrierArea);
                }
            }
        },
        deleteCarrierArea(carrierArea) {
            const index = this.carrier.areas.findIndex(area => area.id === carrierArea.id);
            this.carrier.areas.splice(index, 1);
            for (const destination of this.carrier.destinations) {
                if (destination.carrierAreaId === carrierArea.id) {
                    destination.carrierAreaId = null;
                    destination.area = new CarrierArea();
                }
            }
        },
        deleteCarrierAreaDestination(areaId, destinationId) {
            const areaIndex = this.carrier.areas.findIndex(a => a.id === areaId);
            if (areaIndex > -1) {
                const destinationIndex = this.carrier.areas[areaIndex].destinations.findIndex(d => d.id === destinationId);
                if (destinationIndex > -1) {
                    this.carrier.areas[areaIndex].destinations.splice(destinationIndex, 1);
                }
            }
        },
    },
    computed: {
        carrierId() {
            return parseInt(this.$route.params.id);
        },
    },
};
</script>

<style scoped>

</style>